// Portal de Autosserviço de Certidões (GEN-3604) — a casca do módulo no host. As TELAS vivem no repo
// próprio (Taxlybr/autosservico, pacote @taxlybr/autosservico-web), como a Base Cadastral; aqui ficam
// só a decisão de ONDE o portal monta (`/portal/*` ou a raiz do subdomínio — `portalHost.ts`) e a
// entrega, para a lib, do que é da plataforma: design system, cliente HTTP com sessão, login e MSAL.
//
// Por que a lib recebe tudo isso em vez de trazer o próprio: o portal usa a MESMA sessão da
// plataforma (cookie `token` + fallback + store) e os mesmos componentes lovable. Copiá-los para o
// repo novo criaria uma segunda regra de sessão e uma segunda cópia do design system.
//
// Por que é COMPONENTE e não `mount(el)` como o Kanban 2.0: as rotas do portal são sub-rotas do
// router do host, e o login usa `useMsal`/`useCookies` — que precisam do `MsalProvider` e do
// `CookiesProvider` do host. Num root separado eles ficariam fora dessas árvores.
import { Component, ReactNode, Suspense, lazy, useMemo } from "react";
import { ApiCall } from "../../_services/api";
import { getIsSSO, postLogin, postLoginSSO } from "../../_services/auth.service";
import {
  clearSessionToken,
  persistSessionToken,
  readSessionFallback,
  tokenCookieOptions,
} from "../../_services/authClient";
import { loginRequest } from "../../authConfig";
import { Alert, AlertDescription } from "../../components/ui/lovable/alert";
import { Badge } from "../../components/ui/lovable/badge";
import { Button } from "../../components/ui/lovable/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/lovable/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../components/ui/lovable/dropdown-menu";
import { Input } from "../../components/ui/lovable/input";
import { Label } from "../../components/ui/lovable/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/lovable/select";
import { Skeleton } from "../../components/ui/lovable/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/lovable/table";
import { Textarea } from "../../components/ui/lovable/textarea";
import LoadingComponent from "../../components/loading";
import useLoginToken from "../../store/login.store";
import { authThrottleMessage } from "../../utils/authThrottleMessage";
import { portalApiAuth } from "./portalApiAuth";
import { portalMsalRedirectUri, portalPath } from "./portalHost";
import type { AutosservicoHostBridge } from "@taxlybr/autosservico-web";

// Carga sob demanda: o portal só entra no navegador de quem abre /portal (ou o subdomínio). Antes da
// extração ele viajava dentro do bundle principal, que todo usuário da plataforma baixa no login.
const Autosservico = lazy(() => import("@taxlybr/autosservico-web").then((m) => ({ default: m.Autosservico })));

/**
 * Rede de segurança do carregamento sob demanda: `lazy` que falha ao buscar o arquivo LANÇA durante
 * o render e o host não tem error boundary — sem isto a aplicação inteira cai em branco. O cenário é
 * real durante um rollout (duas versões servidas ao mesmo tempo). O portal não tem Chakra, então o
 * aviso é em HTML simples, na mesma família visual das telas.
 */
class ChunkErrorBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  render() {
    if (!this.state.failed) return this.props.children;
    return (
      <div className="mx-auto mt-16 max-w-md rounded-xl border border-slate-200 bg-white p-6 text-center text-sm text-slate-700">
        <p className="font-semibold text-slate-900">Não foi possível carregar o portal.</p>
        <p className="mt-2">
          Isso costuma acontecer logo depois de uma atualização do sistema — recarregue a página para pegar a versão
          nova.
        </p>
      </div>
    );
  }
}

/**
 * GEN-3604 — montado em `/portal/*` FORA do `AppLayout` (nenhum menu ou rota da plataforma é acessível
 * por aqui) ou em `/*` no subdomínio `autosservico.taxly.com.br` (ver `App.tsx`).
 */
export default function PortalModule() {
  // Memoizada: componentes, serviços e utilitários são referências de módulo; o token não viaja no
  // objeto — chega pelo hook, lido a cada render das telas.
  const host = useMemo<AutosservicoHostBridge>(
    () => ({
      components: {
        Alert,
        AlertDescription,
        Badge,
        Button,
        Dialog,
        DialogContent,
        DialogDescription,
        DialogFooter,
        DialogHeader,
        DialogTitle,
        DropdownMenu,
        DropdownMenuContent,
        DropdownMenuItem,
        DropdownMenuTrigger,
        Input,
        Label,
        Select,
        SelectContent,
        SelectItem,
        SelectTrigger,
        SelectValue,
        Skeleton,
        Table,
        TableBody,
        TableCell,
        TableHead,
        TableHeader,
        TableRow,
        Textarea,
      },
      ApiCall,
      // Não é o `ApiAuth` cru (20 min de timeout) — é a fábrica com teto de 60 s e
      // `overrides` por operação. Contrato documentado em `portalApiAuth.ts`.
      ApiAuth: portalApiAuth,
      // As respostas do login da plataforma são mais largas (2FA, aguardando validação); a lib trata
      // `accessToken` ausente como "não logou" — o cast só estreita o tipo, não muda o dado.
      auth: {
        getIsSSO,
        postLogin: postLogin as AutosservicoHostBridge["auth"]["postLogin"],
        postLoginSSO: postLoginSSO as AutosservicoHostBridge["auth"]["postLoginSSO"],
      },
      session: {
        persistSessionToken,
        readSessionFallback,
        clearSessionToken,
        tokenCookieOptions,
        // Hook, não valor: a lib chama dentro do render e reage à troca de token.
        useLoginToken: () => {
          const { token, setToken } = useLoginToken();
          return { token, setToken };
        },
      },
      // GEN-3604: o popup do Microsoft precisa voltar para o login DO PORTAL neste origin — tanto no
      // host dedicado (autosservico.taxly.com.br → `/login`) quanto em `/portal/*` na plataforma
      // (→ `/portal/login`). Reutilizar o `redirectUri` global deixava o popup preso num redirect
      // cross-origin no host próprio e, sem `REACT_APP_MSAL_REDIRECT_URI`, mandava o portal por
      // caminho para a URL fixa de produção. Regra e override pela env: `portalMsalRedirectUri`.
      // ⚠️ Os dois URIs precisam estar registrados no app do Entra (card GEN-3722); sem isso o Entra
      // recusa o login com AADSTS50011.
      msalLoginRequest: { ...loginRequest, redirectUri: portalMsalRedirectUri() },
      authThrottleMessage: (retryAfter?: number) => authThrottleMessage(retryAfter),
      portalPath,
    }),
    [],
  );

  return (
    <ChunkErrorBoundary>
      <Suspense fallback={<LoadingComponent />}>
        <Autosservico host={host} />
      </Suspense>
    </ChunkErrorBoundary>
  );
}
